import { capitalizeFirstGroceryWord } from './groceryItemDisplayName.js'

/**
 * Recipe provenance on a shopping-list line.
 * One physical line can belong to several meals. Shop groups that line under each meal.
 */

function cleanId(value) {
  if (value == null) return ''
  return String(value).trim()
}

function cleanName(value) {
  return String(value || '').trim()
}

function normalizeName(value) {
  return cleanName(value).toLowerCase()
}

/**
 * @param {Record<string, unknown> | null | undefined} item
 * @returns {{ id: string, name: string }[]}
 */
export function recipeSourcesFromItem(item) {
  const ids = Array.isArray(item?.source_recipe_ids) ? item.source_recipe_ids : null
  const names = Array.isArray(item?.source_recipe_names) ? item.source_recipe_names : null
  if ((ids && ids.length) || (names && names.length)) {
    const count = Math.max(ids?.length || 0, names?.length || 0)
    const sources = []
    for (let i = 0; i < count; i += 1) {
      const id = ids && i < ids.length ? cleanId(ids[i]) : ''
      const indexedName = names && i < names.length ? cleanName(names[i]) : ''
      const fallbackName = names && names.length ? cleanName(names[0]) : ''
      const name = indexedName || fallbackName || 'Recipe'
      if (!id && !indexedName && !fallbackName) continue
      sources.push({ id, name })
    }
    return dedupeSources(sources)
  }
  const singleName = cleanName(item?.source_recipe_name)
  if (!singleName) return []
  return [{ id: cleanId(item?.source_recipe_id), name: singleName }]
}

function sourcesReferToSameRecipe(a, b) {
  if (a.id && b.id) return a.id === b.id
  const aName = normalizeName(a.name)
  const bName = normalizeName(b.name)
  if (!aName || !bName || aName === 'recipe' || bName === 'recipe') return false
  return aName === bName
}

function preferSource(current, incoming) {
  if (!current.id && incoming.id) {
    return { id: incoming.id, name: cleanName(incoming.name) || current.name }
  }
  if ((normalizeName(current.name) === 'recipe' || !cleanName(current.name)) && cleanName(incoming.name)) {
    return { id: current.id || incoming.id, name: cleanName(incoming.name) }
  }
  return current
}

function dedupeSources(sources) {
  /** @type {{ id: string, name: string }[]} */
  const merged = []
  for (const source of sources) {
    const index = merged.findIndex((existing) => sourcesReferToSameRecipe(existing, source))
    if (index === -1) {
      merged.push({ id: source.id, name: source.name })
      continue
    }
    merged[index] = preferSource(merged[index], source)
  }
  return merged
}

function sameSourceList(a, b) {
  if (a.length !== b.length) return false
  return a.every((source, index) => source.id === b[index].id && source.name === b[index].name)
}

/**
 * @param {Record<string, unknown>} item
 * @param {{ id: string, name: string }[]} sources
 */
export function itemWithRecipeSources(item, sources) {
  const next = { ...item }
  if (!sources.length) {
    delete next.source_recipe_id
    delete next.source_recipe_name
    delete next.source_recipe_ids
    delete next.source_recipe_names
    return next
  }
  const primary = sources[0]
  next.source_recipe_ids = sources.map((source) => source.id || '')
  next.source_recipe_names = sources.map((source) => source.name || '')
  if (primary.id) next.source_recipe_id = primary.id
  else delete next.source_recipe_id
  next.source_recipe_name = primary.name || ''
  return next
}

/**
 * Union recipe provenance onto an existing line. Quantity and identity stay as stored.
 * @param {Record<string, unknown>} existing
 * @param {Record<string, unknown> | null | undefined} incoming
 */
export function mergeRecipeSourceOntoItem(existing, incoming) {
  if (!existing || typeof existing !== 'object') return existing
  const incomingSources = recipeSourcesFromItem(incoming)
  if (!incomingSources.length) return existing
  const merged = dedupeSources([...recipeSourcesFromItem(existing), ...incomingSources])
  if (sameSourceList(merged, recipeSourcesFromItem(existing))) return existing
  return itemWithRecipeSources(existing, merged)
}

/**
 * Stamp one recipe onto a line that does not already name a meal.
 * Fills the multi-recipe arrays from the primary source when they are empty.
 * @param {Record<string, unknown>} item
 * @param {{ recipeId?: string | null, recipeName?: string | null }} source
 */
export function stampSingleRecipeSource(item, { recipeId = null, recipeName = '' } = {}) {
  if (!item || typeof item !== 'object') return item
  const name = cleanName(recipeName)
  if (!name) return item
  const id = cleanId(recipeId)
  const next = { ...item }
  if (!cleanName(next.source_recipe_name)) next.source_recipe_name = name
  if (!cleanId(next.source_recipe_id) && id) next.source_recipe_id = id
  const hasArrays =
    (Array.isArray(next.source_recipe_ids) && next.source_recipe_ids.length > 0) ||
    (Array.isArray(next.source_recipe_names) && next.source_recipe_names.length > 0)
  if (!hasArrays) {
    const primaryId = cleanId(next.source_recipe_id)
    const primaryName = cleanName(next.source_recipe_name) || name
    next.source_recipe_ids = primaryId ? [primaryId] : []
    next.source_recipe_names = primaryName ? [primaryName] : []
  }
  if (
    next.source_recipe_name === item.source_recipe_name &&
    next.source_recipe_id === item.source_recipe_id &&
    next.source_recipe_ids === item.source_recipe_ids &&
    next.source_recipe_names === item.source_recipe_names
  ) {
    return item
  }
  return next
}

function sourceMatchesRemoval(source, recipeId, recipeName) {
  if (recipeId) return source.id === recipeId
  if (!recipeName) return false
  return !source.id && normalizeName(source.name) === recipeName
}

/**
 * Drop one meal from the list.
 * Lines that only belong to that meal are removed.
 * Lines other meals still need stay, with this meal taken off their sources.
 * @param {Array<Record<string, unknown>>} items
 * @param {{ recipeId?: string, recipeName?: string }} target
 */
export function detachRecipeFromShoppingList(items, { recipeId = '', recipeName = '' } = {}) {
  const id = cleanId(recipeId)
  const name = normalizeName(recipeName)
  const removedItems = []
  const keptSharedItems = []
  const nextItems = []
  for (const item of items || []) {
    const sources = recipeSourcesFromItem(item)
    if (!sources.some((source) => sourceMatchesRemoval(source, id, name))) {
      nextItems.push(item)
      continue
    }
    const remaining = sources.filter((source) => !sourceMatchesRemoval(source, id, name))
    if (!remaining.length) {
      removedItems.push(item)
      continue
    }
    keptSharedItems.push(item)
    nextItems.push(itemWithRecipeSources(item, remaining))
  }
  return { items: nextItems, removedItems, keptSharedItems }
}

function sharedItemLabels(items) {
  const seen = new Set()
  const labels = []
  for (const item of items || []) {
    const label = capitalizeFirstGroceryWord(item?.item || item?.name || '')
    const key = label.toLowerCase()
    if (!label || seen.has(key)) continue
    seen.add(key)
    labels.push(label)
  }
  return labels
}

/**
 * @param {Array<Record<string, unknown>>} items
 * @returns {string}
 */
export function sharedItemsStayLine(items) {
  const names = sharedItemLabels(items)
  if (!names.length) return ''
  if (names.length === 1) {
    return `${names[0]} stays — another meal still needs it.`
  }
  let list = ''
  if (names.length === 2) list = `${names[0]} and ${names[1]}`
  else if (names.length === 3) list = `${names[0]}, ${names[1]}, and ${names[2]}`
  else list = `${names[0]}, ${names[1]}, and ${names.length - 2} more`
  return `${list} stay — other meals still need them.`
}

/**
 * @param {string} recipeName
 * @param {number} removedCount
 * @param {Array<Record<string, unknown>>} keptSharedItems
 */
export function recipeRemovalConfirmCopy(recipeName, removedCount, keptSharedItems) {
  const name = cleanName(recipeName) || 'This meal'
  const count = Number(removedCount) || 0
  const removedLine =
    count === 0
      ? 'Nothing comes off the list.'
      : count === 1
        ? '1 item comes off.'
        : `${count} items come off.`
  const sharedLine = sharedItemsStayLine(keptSharedItems)
  return {
    title: `Take ${name} off the list?`,
    description: sharedLine ? `${removedLine} ${sharedLine}` : removedLine,
    confirmLabel: 'Take off the list',
  }
}
